import React, { useState } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import { CURRENCY_CODE, CURRENCY_SYMBOL } from '../../constants';
import { isLocalCurrency } from '../../utils';
import SupplierOrderModal from './SupplierOrderModal';

const SUPPLIER_STATUS = {
  NEW_ORDER: 'new_order',
  PREPARING: 'preparing',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled'
};

const SUPPLIER_STATUS_LABELS = {
  [SUPPLIER_STATUS.NEW_ORDER]: 'Yeni Sipariş',
  [SUPPLIER_STATUS.PREPARING]: 'Hazırlanıyor',
  [SUPPLIER_STATUS.SHIPPED]: 'Kargoya Verildi',
  [SUPPLIER_STATUS.DELIVERED]: 'Teslim Edildi',
  [SUPPLIER_STATUS.CANCELLED]: 'İptal'
};

const SUPPLIER_STATUS_CLASS = {
  [SUPPLIER_STATUS.NEW_ORDER]: 'status-new',
  [SUPPLIER_STATUS.PREPARING]: 'status-production',
  [SUPPLIER_STATUS.SHIPPED]: 'status-ready',
  [SUPPLIER_STATUS.DELIVERED]: 'status-completed',
  [SUPPLIER_STATUS.CANCELLED]: 'status-cancelled'
};

const CURRENCY_OPTIONS = [
  { value: 'KZT', label: `${CURRENCY_SYMBOL} ${CURRENCY_CODE}`, symbol: CURRENCY_SYMBOL },
  { value: 'USD', label: '$ USD', symbol: '$' },
  { value: 'EUR', label: '€ EUR', symbol: '€' }
];

const CURRENCY_SYMBOLS = { KZT: CURRENCY_SYMBOL, TRY: CURRENCY_SYMBOL, USD: '$', EUR: '€' };

const fmtCurrency = (v, currency = 'KZT') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  const num = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
  return isLocalCurrency(currency) ? `${num} ${CURRENCY_SYMBOL}` : `${sym} ${num}`;
};

const SupplierSessionPanel = ({
  dealer,
  db,
  user,
  sessions,
  setSessions,
  isLoading,
  fetchSessions,
  onCustomerChange
}) => {
  const [selectedSession, setSelectedSession] = useState(null);
  const [deleteSessionConfirm, setDeleteSessionConfirm] = useState(null);
  const [sessionPage, setSessionPage] = useState(1);
  const SESSION_PAGE_SIZE = 10;

  // Kur seçimi — yeni alım listesi oluştururken kullanılacak
  const [newSessionCurrency, setNewSessionCurrency] = useState('KZT');

  const handleCreateSession = async () => {
    if (!dealer) return;
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const sessionData = {
        dealerId: dealer.id,
        dealerName: dealer.companyName,
        sessionDate: dateStr,
        currency: newSessionCurrency,
        orderStatus: SUPPLIER_STATUS.NEW_ORDER,
        paidAmount: 0,
        isPaymentCompleted: false,
        remainingAmount: 0,
        totalOrderAmount: 0,
        paymentHistory: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null
      };

      const docRef = await addDoc(collection(db, 'supplier_sessions'), sessionData);
      const newSession = { id: docRef.id, ...sessionData };
      setSessions(prev => [newSession, ...prev]);

      setSelectedSession(newSession);
      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Tedarikçi oturumu oluşturulurken hata:", error);
    }
  };

  const handleDeleteSession = async () => {
    if (!deleteSessionConfirm) return;
    try {
      const ordersQuery = query(
        collection(db, 'supplier_orders'),
        where('sessionId', '==', deleteSessionConfirm.id)
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const deletePromises = ordersSnapshot.docs.map(orderDoc =>
        deleteDoc(doc(db, 'supplier_orders', orderDoc.id))
      );
      await Promise.all(deletePromises);

      await deleteDoc(doc(db, 'supplier_sessions', deleteSessionConfirm.id));

      setSessions(prev => prev.filter(s => s.id !== deleteSessionConfirm.id));
      setDeleteSessionConfirm(null);
      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Tedarikçi oturumu silinirken hata:", error);
    }
  };

  const handleStatusChange = async (sessionId, newStatus) => {
    try {
      await updateDoc(doc(db, 'supplier_sessions', sessionId), {
        orderStatus: newStatus,
        updatedAt: new Date()
      });
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? { ...s, orderStatus: newStatus } : s
      ));
      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (err) {
      console.error('Durum güncellenemedi:', err);
    }
  };

  // Özet kartları: aynı para birimindeki session'ları grupla
  const currencyGroups = sessions.reduce((acc, ss) => {
    const cur = ss.currency || 'KZT';
    if (!acc[cur]) acc[cur] = { total: 0, paid: 0, remaining: 0 };
    acc[cur].total += parseFloat(ss.totalOrderAmount || 0);
    acc[cur].paid += parseFloat(ss.paidAmount || 0);
    acc[cur].remaining += parseFloat(ss.remainingAmount ?? Math.max(0, parseFloat(ss.totalOrderAmount || 0) - parseFloat(ss.paidAmount || 0)));
    return acc;
  }, {});
  const currencyGroupKeys = Object.keys(currencyGroups);

  return (
    <div className="dealer-sessions-section supplier-sessions-panel">
      <div className="sessions-header">
        <h4>
          <i className="fas fa-truck-loading"></i> Tedarikçi Alım Listeleri
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Kur seçimi */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>
              <i className="fas fa-coins" style={{ marginRight: '4px', color: '#8b5cf6' }}></i>Kur:
            </label>
            <select
              value={newSessionCurrency}
              onChange={(e) => setNewSessionCurrency(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1.5px solid #ddd6fe',
                borderRadius: '8px',
                background: '#f5f3ff',
                color: '#6d28d9',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {CURRENCY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button
            className="add-session-btn"
            onClick={handleCreateSession}
            style={{ background: '#8b5cf6', boxShadow: '0 4px 10px rgba(139, 92, 246, 0.2)' }}
          >
            <i className="fas fa-plus-circle"></i> Yeni Alım Listesi
          </button>
        </div>
      </div>

      {/* Özet kartlar — para birimine göre gruplu */}
      {sessions.length > 0 && (
        <>
          {currencyGroupKeys.map(cur => (
            <div key={cur} className="dealer-financial-summary" style={{ marginBottom: currencyGroupKeys.length > 1 ? '8px' : undefined }}>
              {currencyGroupKeys.length > 1 && (
                <div style={{ gridColumn: '1/-1', fontSize: '0.78rem', fontWeight: 700, color: '#8b5cf6', marginBottom: '-5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ background: '#ede9fe', padding: '2px 8px', borderRadius: '12px' }}>{CURRENCY_SYMBOLS[cur] || cur} {cur}</span>
                </div>
              )}
              <div className="financial-card total-card" style={{ background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', borderColor: '#ddd6fe' }}>
                <div className="financial-card-icon" style={{ background: '#ddd6fe', color: '#6b21a8' }}><i className="fas fa-file-invoice-dollar"></i></div>
                <div className="financial-card-info">
                  <span className="financial-card-label" style={{ color: '#581c87' }}>Toplam Alım Tutarı</span>
                  <span className="financial-card-value" style={{ color: '#6b21a8' }}>{fmtCurrency(currencyGroups[cur].total, cur)}</span>
                </div>
              </div>
              <div className="financial-card paid-card" style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderColor: '#bbf7d0' }}>
                <div className="financial-card-icon" style={{ background: '#dcfce7', color: '#166534' }}><i className="fas fa-hand-holding-usd"></i></div>
                <div className="financial-card-info">
                  <span className="financial-card-label" style={{ color: '#14532d' }}>Ödenen (Bizim Ödediğimiz)</span>
                  <span className="financial-card-value" style={{ color: '#16a34a' }}>{fmtCurrency(currencyGroups[cur].paid, cur)}</span>
                </div>
              </div>
              <div className="financial-card remaining-card" style={{ background: 'linear-gradient(135deg, #fef2f2, #fee2e2)', borderColor: '#fca5a5' }}>
                <div className="financial-card-icon" style={{ background: '#fee2e2', color: '#991b1b' }}><i className="fas fa-exclamation-circle"></i></div>
                <div className="financial-card-info">
                  <span className="financial-card-label" style={{ color: '#7f1d1d' }}>Kalan Borcumuz</span>
                  <span className="financial-card-value" style={{ color: '#dc2626' }}>{fmtCurrency(currencyGroups[cur].remaining, cur)}</span>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {isLoading ? (
        <div className="dealer-loading-container">
          <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#94a3b8' }}></i>
          <p style={{ color: '#64748b', marginTop: '10px' }}>Alım listeleri yükleniyor...</p>
        </div>
      ) : sessions.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-box-open" style={{ color: '#ddd6fe' }}></i>
          <p>Henüz alım listesi bulunmuyor. Yeni bir alım listesi oluşturarak tedarikçi alımlarınızı kaydedin.</p>
        </div>
      ) : (
        <table className="sessions-table">
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Kur</th>
              <th>Durum</th>
              <th>Toplam Alım</th>
              <th>Ödenen / Borcumuz</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {sessions
              .slice((sessionPage - 1) * SESSION_PAGE_SIZE, sessionPage * SESSION_PAGE_SIZE)
              .map(session => {
                const cur = session.currency || 'KZT';
                const totalAmt = parseFloat(session.totalOrderAmount || 0);
                const paid = parseFloat(session.paidAmount || 0);
                const remaining = parseFloat(session.remainingAmount ?? (totalAmt - paid));
                const status = session.orderStatus || SUPPLIER_STATUS.NEW_ORDER;
                const statusClass = SUPPLIER_STATUS_CLASS[status] || 'status-new';

                const sessionCreatedDate = session.createdAt?.toDate ? session.createdAt.toDate() : (session.createdAt ? new Date(session.createdAt) : new Date());

                return (
                  <tr key={session.id}>
                    <td className="session-date">
                      {sessionCreatedDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: isLocalCurrency(cur) ? '#f0fdf4' : cur === 'USD' ? '#eff6ff' : '#fdf4ff',
                        color: isLocalCurrency(cur) ? '#166534' : cur === 'USD' ? '#1d4ed8' : '#7e22ce',
                        border: `1px solid ${isLocalCurrency(cur) ? '#bbf7d0' : cur === 'USD' ? '#bfdbfe' : '#e9d5ff'}`,
                        borderRadius: '6px',
                        padding: '3px 8px',
                        fontSize: '0.82rem',
                        fontWeight: 700
                      }}>
                        {CURRENCY_SYMBOLS[cur] || cur} {cur}
                      </span>
                    </td>
                    <td>
                      <select
                        className={`session-status-select ${statusClass}`}
                        value={status}
                        onChange={(e) => handleStatusChange(session.id, e.target.value)}
                      >
                        {Object.entries(SUPPLIER_STATUS_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="finance-cell text-gray">
                      {fmtCurrency(totalAmt, cur)}
                    </td>
                    <td className="finance-cell">
                      <span className="text-green">{fmtCurrency(paid, cur)}</span>
                      <span style={{ color: '#cbd5e1', margin: '0 8px' }}>|</span>
                      <span className="text-red">{fmtCurrency(remaining, cur)}</span>
                    </td>
                    <td className="session-actions">
                      <button
                        type="button"
                        className="btn-detail"
                        onClick={() => setSelectedSession(session)}
                        style={{ border: '1px solid #ddd6fe', background: '#f5f3ff' }}
                      >
                        <i className="fas fa-external-link-alt" style={{ color: '#8b5cf6' }}></i> Alımları Gör
                      </button>
                      <button
                        type="button"
                        className="btn-delete-session"
                        onClick={() => setDeleteSessionConfirm(session)}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      )}

      {/* Sayfalama */}
      {sessions.length > SESSION_PAGE_SIZE && (
        <div className="sessions-pagination">
          <button
            type="button"
            className="page-btn"
            onClick={() => setSessionPage(p => Math.max(1, p - 1))}
            disabled={sessionPage === 1}
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          {Array.from({ length: Math.ceil(sessions.length / SESSION_PAGE_SIZE) }, (_, i) => i + 1).map(page => (
            <button
              type="button"
              key={page}
              className={`page-btn ${sessionPage === page ? 'active' : ''}`}
              onClick={() => setSessionPage(page)}
              style={{
                background: sessionPage === page ? '#8b5cf6' : '#f8fafc',
                borderColor: sessionPage === page ? '#8b5cf6' : '#e2e8f0'
              }}
            >
              {page}
            </button>
          ))}

          <button
            type="button"
            className="page-btn"
            onClick={() => setSessionPage(p => Math.min(Math.ceil(sessions.length / SESSION_PAGE_SIZE), p + 1))}
            disabled={sessionPage === Math.ceil(sessions.length / SESSION_PAGE_SIZE)}
          >
            <i className="fas fa-chevron-right"></i>
          </button>

          <span className="page-info">
            {(sessionPage - 1) * SESSION_PAGE_SIZE + 1}–{Math.min(sessionPage * SESSION_PAGE_SIZE, sessions.length)} / {sessions.length} kayıt
          </span>
        </div>
      )}

      {/* Silme Onayı */}
      {deleteSessionConfirm && (
        <div className="order-details-modal-overlay" style={{ zIndex: 1100 }}>
          <div className="order-details-modal-content" style={{ maxWidth: '500px', height: 'auto', padding: '30px' }}>
            <h3 style={{ color: '#ef4444', marginBottom: '15px' }}><i className="fas fa-exclamation-triangle"></i> Alım Listesini Sil</h3>
            <p style={{ color: '#475569', lineHeight: '1.6' }}>
              Bu alım listesini ve içindeki tüm ürün alımlarını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '25px' }}>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setDeleteSessionConfirm(null)}
              >
                İptal
              </button>
              <button
                type="button"
                className="save-btn"
                style={{ background: '#dc2626' }}
                onClick={handleDeleteSession}
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tedarikçi Alım Detayları Modal */}
      {selectedSession && (
        <SupplierOrderModal
          session={selectedSession}
          onClose={() => {
            setSelectedSession(null);
            fetchSessions(dealer.id);
            if (onCustomerChange) {
              onCustomerChange();
            }
          }}
          db={db}
          user={user}
        />
      )}
    </div>
  );
};

export default SupplierSessionPanel;
